/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   Login ลูกค้า (เข้าดูโปรเจ็กต์ของตัวเอง + แจ้ง RC ที่ /portal)
   Support สร้างให้ — ระบบสุ่มรหัสผ่าน 6 ตัว (ตัวอักษร + ตัวเลข) แสดงครั้งเดียว
============================================================ */
import React, { useEffect, useState } from "react";
import { Alert, Button, Descriptions, Flex, Input, Modal, Popconfirm, Space, Spin, Tag, Typography, message } from "antd";
import { CopyOutlined, KeyOutlined, StopOutlined, CheckCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { CustomerPortalService, apiError } from "../../../service/Ak.service";
import { usePermission } from "../../../store/context/permission-context";

/* ลูกค้า login หน้าเดียวกับพนักงาน แล้วระบบพาไปหน้าบ้านของตัวเอง */
export const portalUrl = () => `${window.location.origin}/login`;

const copy = (text) =>
  navigator.clipboard
    ? navigator.clipboard.writeText(text).then(() => message.success("คัดลอกแล้ว"))
    : message.info("คัดลอกไม่ได้ในเบราว์เซอร์นี้ กรุณาเลือกข้อความแล้วคัดลอกเอง");

export default function PortalLoginModal({ customer, open, onClose, onChanged }) {
  const { can } = usePermission();
  const canEdit = can("support_customer", "edit");
  const [info, setInfo] = useState(null);
  const [username, setUsername] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    setInfo(null);
    setResult(null);
    CustomerPortalService.get(customer.cus_code)
      .then((r) => {
        setInfo(r.data.data);
        setUsername(r.data.data.portal_username || "");
      })
      .catch((e) => message.error(apiError(e)));
  }, [open, customer?.cus_code]);

  const reset = async () => {
    setBusy(true);
    try {
      const r = await CustomerPortalService.reset(customer.cus_code, username.trim());
      setResult(r.data.data);
      setInfo(r.data.data);
      onChanged?.();
    } catch (e) {
      message.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const setActive = async (active) => {
    setBusy(true);
    try {
      const r = await CustomerPortalService.setActive(customer.cus_code, active);
      setInfo(r.data.data);
      message.success(active === "Y" ? "เปิดการใช้งานแล้ว" : "ปิดการใช้งานแล้ว — ลูกค้าจะถูกออกจากระบบทันที");
      onChanged?.();
    } catch (e) {
      message.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const name = [customer?.prename, customer?.cus_name].filter(Boolean).join(" ");
  const msg = result
    ? `เรียน ${name}\nAK HOME เปิดระบบติดตามงานสร้างบ้านให้แล้ว\n\nเข้าใช้งาน: ${portalUrl()}\nชื่อผู้ใช้: ${result.portal_username}\nรหัสผ่าน: ${result.password}\n\nดูแบบบ้าน เอกสาร และแจ้งขอเปลี่ยนแปลง (RC) ได้ที่นี่\nเปลี่ยนรหัสผ่านเองได้หลังเข้าระบบ`
    : "";
  const hasLogin = info?.has_password;

  return (
    <Modal open={open} onCancel={onClose} title={`Login ลูกค้า · ${name}`} footer={<Button onClick={onClose}>ปิด</Button>} width={560} destroyOnClose>
      {!info ? (
        <Flex justify="center" style={{ padding: 32 }}>
          <Spin />
        </Flex>
      ) : (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            ลูกค้า login ที่ <Typography.Text code>{portalUrl()}</Typography.Text> หน้าเดียวกับพนักงาน แล้วระบบพาไปหน้าบ้านของตัวเอง — เห็นเฉพาะโปรเจ็กต์ของตัวเอง ดูแบบบ้าน/ไฟล์ และแจ้ง RC ได้
          </Typography.Text>

          {hasLogin && !result && (
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="ชื่อผู้ใช้">{info.portal_username}</Descriptions.Item>
              <Descriptions.Item label="สถานะ">
                {info.portal_active === "Y" ? <Tag color="green">ใช้งานได้</Tag> : <Tag color="red">ปิดการใช้งาน</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="เข้าใช้ล่าสุด">{info.portal_last_login ? dayjs(info.portal_last_login).format("DD/MM/YYYY HH:mm") : "ยังไม่เคยเข้า"}</Descriptions.Item>
            </Descriptions>
          )}

          {result && (
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="สร้างรหัสผ่านแล้ว — คัดลอกส่งให้ลูกค้าตอนนี้ (ปิดหน้าต่างนี้แล้วจะดูรหัสอีกไม่ได้)"
              description={
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Flex gap={16} wrap="wrap" className="ak-portal-cred">
                    <div>
                      <div className="lbl">ชื่อผู้ใช้</div>
                      <div className="val">{result.portal_username}</div>
                    </div>
                    <div>
                      <div className="lbl">รหัสผ่าน</div>
                      <div className="val">{result.password}</div>
                    </div>
                  </Flex>
                  <Input.TextArea value={msg} autoSize readOnly />
                  <Button type="primary" icon={<CopyOutlined />} onClick={() => copy(msg)}>
                    คัดลอกข้อความส่ง LINE
                  </Button>
                </Space>
              }
            />
          )}

          {canEdit && !result && (
            <>
              <div>
                <Typography.Text strong>ชื่อผู้ใช้</Typography.Text>
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/\s/g, "").toLowerCase())}
                  placeholder="เว้นว่าง = ใช้เบอร์โทรลูกค้า"
                  maxLength={50}
                  style={{ marginTop: 4 }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  a-z 0-9 . _ - @ อย่างน้อย 4 ตัว · ค่าเริ่มต้นคือเบอร์โทรลูกค้า · ห้ามซ้ำกับ username พนักงาน
                </Typography.Text>
              </div>
              <Flex gap={8} wrap="wrap">
                {hasLogin ? (
                  <Popconfirm title="สุ่มรหัสผ่านใหม่?" description="รหัสเดิมใช้ไม่ได้ทันที ลูกค้าต้องเข้าใหม่ด้วยรหัสใหม่" onConfirm={reset} okText="สุ่มรหัสใหม่" cancelText="ไม่">
                    <Button type="primary" icon={<KeyOutlined />} loading={busy}>
                      รีเซ็ตรหัสผ่าน
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button type="primary" icon={<KeyOutlined />} loading={busy} onClick={reset}>
                    สร้าง Login + สุ่มรหัสผ่าน
                  </Button>
                )}
                {hasLogin && info.portal_active === "Y" && (
                  <Popconfirm title="ปิดการใช้งาน login นี้?" onConfirm={() => setActive("N")} okText="ปิด" cancelText="ไม่">
                    <Button danger icon={<StopOutlined />} loading={busy}>
                      ปิดการใช้งาน
                    </Button>
                  </Popconfirm>
                )}
                {hasLogin && info.portal_active !== "Y" && (
                  <Button loading={busy} onClick={() => setActive("Y")}>
                    เปิดใช้งานอีกครั้ง
                  </Button>
                )}
              </Flex>
            </>
          )}
        </Space>
      )}
    </Modal>
  );
}
